import { type HistoryEntry, type ListenStat, parseHistory, parseStats } from './history';
import { findByUrl, parseStations, renumber } from './library';
import { migrate, SCHEMA_VERSION } from './migrations';
import { parseSettings, type Settings } from './settings';
import type { Station } from './stations';

/**
 * Izvoz in uvoz (T2 §7).
 *
 * **Izvoz je prvorazreden, ne dodatek** (T2 §1, načelo 4). Je edina prava
 * varovalka pred izgubo podatkov: `navigator.storage.persist()` ni jamstvo,
 * Safari pa skriptno zapisljivo shrambo izbriše po 7 dneh brez uporabnikove
 * interakcije — tudi za aplikacije z domačega zaslona.
 */

export const BACKUP_FORMAT = 'nesywave-backup';

/**
 * Dve vrsti izvoza (T2 §7).
 *
 * > Stara aplikacija je imela tu past: izvoz »samo postaj« ni vključeval
 * > jezika, teme in EQ, kar je bilo iz imena težko razbrati.
 *
 * Zato sta vrsti poimenovani po tem, **kaj gre zraven**, in vmesnik to pove
 * z besedami, ne le z imenom datoteke.
 */
export type BackupKind = 'stations' | 'full';

export interface Backup {
  format: typeof BACKUP_FORMAT;
  version: number;
  exportedAt: string;
  app: { name: string; version: string; build?: string };
  data: {
    stations: Station[];
    settings?: Settings;
    /** Le v celotni kopiji (T2 §7): »samo postaje« ju namenoma nima. */
    stats?: ListenStat[];
    history?: HistoryEntry[];
  };
}

export interface CreateBackupOptions {
  kind: BackupKind;
  stations: readonly Station[];
  settings: Settings;
  /** Le za celotno kopijo; »samo postaje« ju spusti (T2 §7). */
  stats?: readonly ListenStat[];
  history?: readonly HistoryEntry[];
  /** Epoch ms; podan od zunaj, da je izvoz v testu determinističen. */
  now: number;
  appVersion: string;
  /**
   * Kratek commit sha gradnje, ki je kopijo naredila.
   *
   * ⚠️ **Diagnostika, ne pogoj veljavnosti.** Uvoz ga ne bere in ga ne sme —
   * o združljivosti odloča izključno `SCHEMA_VERSION`. Obstaja zato, ker
   * `version` sama ne pove, katera gradnja je datoteko zapisala, prijava o
   * pokvarjeni kopiji pa se vedno začne pri tem vprašanju.
   */
  buildSha: string;
}

export function createBackup(options: CreateBackupOptions): Backup {
  const backup: Backup = {
    format: BACKUP_FORMAT,
    version: SCHEMA_VERSION,
    exportedAt: new Date(options.now).toISOString(),
    app: { name: 'Nesywave', version: options.appVersion, build: options.buildSha },
    data: { stations: options.stations.map((station) => ({ ...station })) },
  };

  // Celotna kopija je varovalka in selitev na novo napravo: nastavitve,
  // zgodovina in čas gredo zraven — sicer ponovi past stare aplikacije, kjer
  // je bilo iz imena »samo postaje« težko razbrati, česa NI (T2 §7).
  if (options.kind === 'full') {
    backup.data.settings = { ...options.settings };
    backup.data.stats = (options.stats ?? []).map((stat) => ({ ...stat }));
    backup.data.history = (options.history ?? []).map((entry) => ({ ...entry }));
  }
  return backup;
}

/** Ime datoteke z datumom, da se dva izvoza ne prepišeta. */
export function backupFileName(kind: BackupKind, now: number): string {
  const date = new Date(now).toISOString().slice(0, 10);
  return `nesywave-${kind === 'full' ? 'backup' : 'stations'}-${date}.json`;
}

/* ── Branje ────────────────────────────────────────────────────────── */

export type ParseFailure =
  | { reason: 'unreadable' }
  | { reason: 'not-a-backup' }
  | { reason: 'too-new'; version: number }
  | { reason: 'migration-failed'; version: number };

export type ParseResult = { ok: true; backup: Backup } | ({ ok: false } & ParseFailure);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Uvožena datoteka, preverjena in po potrebi migrirana.
 *
 * **Neznana oblika je jasna napaka, ne ugibanje** (T2 §7). Datoteka, ki je
 * ni zapisala ta aplikacija, ima lahko polje `stations` s čisto drugačnim
 * pomenom; poskus branja bi uporabniku tiho pokvaril seznam.
 */
export function parseBackup(text: string): ParseResult {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, reason: 'unreadable' };
  }

  if (!isRecord(raw) || raw.format !== BACKUP_FORMAT) return { ok: false, reason: 'not-a-backup' };

  const version = typeof raw.version === 'number' ? raw.version : 1;
  if (version > SCHEMA_VERSION) return { ok: false, reason: 'too-new', version };

  const data = isRecord(raw.data) ? raw.data : {};

  // Starejša kopija gre skozi iste migracije kot shramba (T2 §7, točka 2) —
  // dve poti do istega rezultata bi se sčasoma razšli.
  const migrated = migrate({ stations: data.stations, settings: data.settings }, version);
  if (!migrated.ok) return { ok: false, reason: 'migration-failed', version };

  const backup: Backup = {
    format: BACKUP_FORMAT,
    version: SCHEMA_VERSION,
    exportedAt: typeof raw.exportedAt === 'string' ? raw.exportedAt : '',
    app: { name: 'Nesywave', version: '' },
    data: { stations: parseStations(migrated.data.stations) },
  };

  // Nastavitve so v kopiji »samo postaje« odsotne; to ni napaka, ampak
  // vrsta izvoza — in uvoz mora znati razlikovati med obojim.
  if (migrated.data.settings !== undefined && migrated.data.settings !== null) {
    backup.data.settings = parseSettings(migrated.data.settings);
  }
  // Zgodovina in čas sta neobvezna in prideta le v celotni kopiji. Bereta se
  // obrambno (parseStats/parseHistory) — tuja datoteka sme imeti karkoli.
  if (data.stats !== undefined) backup.data.stats = parseStats(data.stats);
  if (data.history !== undefined) backup.data.history = parseHistory(data.history);

  return { ok: true, backup };
}

/* ── Predogled in uveljavitev ──────────────────────────────────────── */

export type ImportMode = 'merge' | 'replace';

export interface ImportPreview {
  /** Koliko postaj je v datoteki. */
  total: number;
  /** Koliko jih na seznamu še ni. */
  added: number;
  /** Koliko jih je že — po naslovu, ne po imenu (T2 §7, točka 5). */
  duplicate: number;
  /** Ali bo uvoz prepisal nastavitve. Uporabnik mora to vedeti VNAPREJ. */
  replacesSettings: boolean;
}

/**
 * Kaj se bo zgodilo — **preden se zgodi** (T2 §7, točka 3).
 *
 * Uvoz brez predogleda je nepovraten poseg v edine podatke, ki jih uporabnik
 * ima. Predogled je zato del funkcije, ne dodatek k njej.
 */
export function previewImport(
  current: readonly Station[],
  backup: Backup,
  mode: ImportMode,
): ImportPreview {
  const incoming = backup.data.stations;
  const duplicate =
    mode === 'replace' ? 0 : incoming.filter((s) => findByUrl(current, s.url) !== null).length;

  return {
    total: incoming.length,
    added: incoming.length - duplicate,
    duplicate,
    replacesSettings: backup.data.settings !== undefined,
  };
}

export interface ApplyImportOptions {
  mode: ImportMode;
  /** Nov `id` za postajo, katere id na seznamu že obstaja z drugim naslovom. */
  newId: () => string;
}

/**
 * Uvožene postaje, združene ali zamenjane. **Privzeto združi** (T2 §7, točka 4)
 * — zamenjava je nepovratna in ne sme biti privzeti klik.
 *
 * Podvojene se preskočijo, ne prepišejo: uporabnik je svojo različico morda
 * preimenoval ali označil kot priljubljeno in uvoz mu tega ne sme povoziti.
 */
export function applyImport(
  current: readonly Station[],
  backup: Backup,
  options: ApplyImportOptions,
): Station[] {
  if (options.mode === 'replace') return renumber(backup.data.stations.map((s) => ({ ...s })));

  const merged = [...current];
  const usedIds = new Set(current.map((s) => s.id));

  for (const station of backup.data.stations) {
    if (findByUrl(merged, station.url) !== null) continue;

    // Trk id-jev med dvema napravama je redek, a možen; naslov je različen,
    // zato gre za dve postaji in ena mora dobiti nov id.
    const id = usedIds.has(station.id) ? options.newId() : station.id;
    usedIds.add(id);
    merged.push({ ...station, id, order: merged.length });
  }

  return renumber(merged);
}
